<template>
  <div class="change-password-page">
    <h1>Change Password</h1>
    
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      Processing...
    </div>

    <div v-else-if="isGoogleUser" class="google-user-message">
      <div class="info-box">
        <i class="fab fa-google"></i>
        <p>You are signed in with Google. Password management is handled through your Google Account.</p>
        <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" class="google-link">
          Manage Google Account Security
        </a>
      </div>
    </div>
    
    <div v-else class="change-password-form profile-section">
      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <label for="currentPassword">Current Password</label>
          <div class="password-input-container">
            <input
              :type="showCurrentPassword ? 'text' : 'password'"
              id="currentPassword"
              v-model="formData.currentPassword"
              required
              :class="{ 'error': errors.currentPassword }"
            />
            <button 
              type="button" 
              class="toggle-password"
              @click="showCurrentPassword = !showCurrentPassword"
              :aria-label="showCurrentPassword ? 'Hide password' : 'Show password'"
            >
              <i :class="showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <span class="error-message" v-if="errors.currentPassword">{{ errors.currentPassword }}</span>
        </div>

        <div class="form-group">
          <label for="newPassword">New Password</label>
          <div class="password-input-container">
            <input
              :type="showNewPassword ? 'text' : 'password'"
              id="newPassword"
              v-model="formData.newPassword"
              required
              :class="{ 'error': errors.newPassword }"
            />
            <button 
              type="button" 
              class="toggle-password"
              @click="showNewPassword = !showNewPassword"
              :aria-label="showNewPassword ? 'Hide password' : 'Show password'"
            >
              <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <span class="error-message" v-if="errors.newPassword">{{ errors.newPassword }}</span>
        </div>

        <div class="form-group">
          <label for="confirmPassword">Confirm New Password</label>
          <div class="password-input-container">
            <input
              :type="showConfirmPassword ? 'text' : 'password'"
              id="confirmPassword"
              v-model="formData.confirmPassword"
              required
              :class="{ 'error': errors.confirmPassword }"
            />
            <button 
              type="button" 
              class="toggle-password"
              @click="showConfirmPassword = !showConfirmPassword"
              :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
            >
              <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <span class="error-message" v-if="errors.confirmPassword">{{ errors.confirmPassword }}</span>
        </div>

        <div class="form-actions">
          <button type="submit" :disabled="loading">Change Password</button>
        </div>

        <div v-if="successMessage" class="success-message">
          {{ successMessage }}
        </div>
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';

export default {
  name: 'ChangePassword',
  data() {
    return {
      formData: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      errors: {},
      loading: false,
      successMessage: '',
      errorMessage: '',
      isGoogleUser: false
    }
  },
  async created() {
    // Check if user is authenticated with Google
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/api/auth/check-session`,
        { withCredentials: true }
      );
      
      if (response.data.user && response.data.user.google_id) {
        this.isGoogleUser = true;
      }
    } catch (error) {
      console.error('Error checking user session:', error);
      this.$router.push('/');
    }
  },
  methods: {
    validateForm() {
      this.errors = {};
      
      if (!this.formData.currentPassword) {
        this.errors.currentPassword = 'Current password is required';
      }
      
      if (!this.formData.newPassword) {
        this.errors.newPassword = 'New password is required';
      } else if (this.formData.newPassword.length < 6) {
        this.errors.newPassword = 'Password must be at least 6 characters long';
      }
      
      if (!this.formData.confirmPassword) {
        this.errors.confirmPassword = 'Please confirm your new password';
      } else if (this.formData.newPassword !== this.formData.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match';
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }
      
      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';
      
      try {
        await axios.post(
          `${config.apiBaseUrl}/api/auth/change-password`,
          {
            currentPassword: this.formData.currentPassword,
            newPassword: this.formData.newPassword
          },
          { withCredentials: true }
        );
        
        this.successMessage = 'Password changed successfully!';
        this.formData = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        };
      } catch (error) {
        console.error('Error changing password:', error);
        this.errorMessage = error.response?.data?.error || 'Failed to change password. Please try again.';
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.change-password-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

h1 {
  margin-bottom: 30px;
  color: #333;
  font-size: 24px;
}

.profile-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

h2 {
  margin-bottom: 20px;
  color: #333;
  font-size: 18px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.google-user-message {
  text-align: center;
  padding: 2rem;
}

.info-box {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.info-box i {
  font-size: 3rem;
  color: #4285f4;
  margin-bottom: 1rem;
}

.info-box p {
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.google-link {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: #4285f4;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.google-link:hover {
  background: #3367d6;
}

.form-group {
  margin-bottom: 1.5rem;
  text-align: left;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
  transition: color 0.3s ease;
}

input {
  width: 400px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  display: block;
  margin: 0 auto;
  transition: all 0.3s ease;
}

input:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
}

input.error {
  border-color: #dc3545;
}

input.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  text-align: left;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.success-message {
  color: #28a745;
  text-align: center;
  margin-top: 1rem;
  padding: 0.75rem;
  background: #d4edda;
  border-radius: 4px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.form-actions {
  margin-top: 2rem;
}

button {
  width: 400px;
  padding: 0.75rem;
  background: #0066cc;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
}

button:hover {
  /*background: #0052a3;*/
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

button:active {
  transform: translateY(0);
  box-shadow: none;
}

button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
}

button:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

.loading {
  text-align: center;
  padding: 2rem;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .change-password-page {
    padding: 15px;
  }
  
  .profile-section {
    padding: 15px;
  }

  input, button {
    width: 100%;
    max-width: 100%;
  }

  .form-group {
    width: 100%;
    max-width: 100%;
    padding: 0 15px;
  }

  .error-message,
  .success-message {
    width: 100%;
    max-width: 100%;
    margin-left: 15px;
    margin-right: 15px;
  }

  h1 {
    font-size: 20px;
    padding: 0 15px;
  }

  h2 {
    font-size: 16px;
  }

  .info-box {
    margin: 0 15px;
    padding: 1.5rem;
  }

  .google-link {
    width: 100%;
    text-align: center;
    margin: 0 auto;
  }
}

@media (max-width: 480px) {
  .change-password-page {
    padding: 10px;
  }

  input, button {
    padding: 0.6rem;
    font-size: 0.95rem;
  }

  .form-group {
    margin-bottom: 1rem;
    padding: 0 10px;
  }

  label {
    font-size: 0.9rem;
  }

  .error-message,
  .success-message {
    font-size: 0.8rem;
    margin-left: 10px;
    margin-right: 10px;
  }
}

.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.password-input-container input {
  padding-right: 45px;
  width: 100%;
  margin: 0;
  height: 44px;
  background: #fff;
  transition: border-color 0.2s ease;
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 0;
  height: 100%;
  background: none;
  border: none;
  width: 40px;
  padding: 0;
  cursor: pointer;
  color: #757575;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transform: none;
}

.toggle-password:hover {
  opacity: 1;
}

.toggle-password:focus {
  outline: none;
  opacity: 1;
}

.toggle-password i {
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.form-group:focus-within {
  position: relative;
}

.form-group:focus-within::after {
  content: '';
  position: absolute;
  top: 32px;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 4px;
  pointer-events: none;
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.1);
  z-index: 1;
  transition: box-shadow 0.2s ease;
}

.form-group:focus-within label {
  color: #0066cc;
}

.toggle-password:hover,
.toggle-password:focus {
  /*color: #0066cc;*/
}

/* Success state */
.form-group.success input {
  border-color: #0066cc;
  background-color: #f0f7ff;
}

.form-group.success .toggle-password {
  opacity: 1;
  color: #0066cc;
}

/* Error state */
.form-group.error input {
  border-color: #dc3545;
  background-color: #fff8f8;
}

.form-group.error:focus-within::after {
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

/* Mobile Styles */
@media (max-width: 768px) {
  .password-input-container input {
    height: 48px;
    font-size: 16px;
    padding: 12px;
    padding-right: 45px;
  }

  .toggle-password {
    width: 28px;
    height: 28px;
  }

  .toggle-password i {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .password-input-container input {
    height: 44px;
    padding: 10px;
    padding-right: 40px;
  }

  .toggle-password {
    width: 24px;
    height: 24px;
    right: 10px;
  }

  .toggle-password i {
    font-size: 18px;
  }
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .password-input-container input {
    background-color: #2a2a2a;
    border-color: #444;
    color: #fff;
  }

  .toggle-password {
    color: #888;
  }

  .toggle-password:hover {
    opacity: 1;
  }

  .form-group.success input {
    background-color: rgba(0, 102, 204, 0.1);
  }

  .form-group.error input {
    background-color: rgba(220, 53, 69, 0.1);
  }

  input:focus {
    border-color: #0066cc;
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.3);
  }

  .toggle-password:hover,
  .toggle-password:focus {
    color: #0066cc;
    background-color: rgba(0, 102, 204, 0.15);
  }
}
</style> 